import { z } from "zod";
import { GENRE_SOURCES, STATUS_IDS } from "../constants.ts";
import { DbBool, IsoDate, IsoDateTime, LocalIdSchema, SpotifyIdSchema } from "./common.ts";
import { SORT_DIRECTIONS, SortSchema, type Sort } from "./filter.ts";

/**
 * What set of tracks a query starts from. Everything else — filter, search, sort —
 * narrows this. A scope is a string on the wire so it survives a URL and a saved
 * link: `liked`, `all`, `playlist:<spotifyId>`, `local:<lpl_id>`.
 */
export type QueryScope =
  | { kind: "all" }
  | { kind: "liked" }
  | { kind: "playlist"; id: string }
  | { kind: "local"; id: string };

const SCOPE_PATTERN = /^(?:all|liked|playlist:[A-Za-z0-9]{10,40}|local:lpl_[A-Za-z0-9_-]{8,32})$/;

export const ScopeStringSchema = z
  .string()
  .regex(SCOPE_PATTERN, "Expected all, liked, playlist:<id> or local:<id>");

/** Total order over scope strings, so callers never hand-split on `:`. */
export function parseScope(raw: string): QueryScope {
  if (raw === "all") return { kind: "all" };
  if (raw === "liked") return { kind: "liked" };
  if (raw.startsWith("playlist:")) return { kind: "playlist", id: raw.slice("playlist:".length) };
  if (raw.startsWith("local:")) return { kind: "local", id: raw.slice("local:".length) };
  throw new Error(`Unrecognised scope "${raw}"`);
}

export function formatScope(scope: QueryScope): string {
  switch (scope.kind) {
    case "all":
      return "all";
    case "liked":
      return "liked";
    case "playlist":
      return `playlist:${scope.id}`;
    case "local":
      return `local:${scope.id}`;
  }
}

const SORT_TOKEN = /^[a-z_]+\.[a-z0-9_-]+(?::(?:asc|desc))?$/;

/**
 * `sort` arrives as `field:dir`, repeatable, and comma-joinable so a saved link
 * stays short. Both spellings collapse to the same list.
 */
export function parseSortParam(raw: string | string[] | undefined): Sort[] {
  if (raw === undefined) return [];
  const tokens = (Array.isArray(raw) ? raw : [raw])
    .flatMap((entry) => entry.split(","))
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  return tokens.map((token) => {
    if (!SORT_TOKEN.test(token)) {
      throw new Error(`Invalid sort "${token}", expected field:asc or field:desc`);
    }
    const [field, dir] = token.split(":");
    return SortSchema.parse({ field, dir: dir ?? SORT_DIRECTIONS[0] });
  });
}

export const TrackQueryParamsSchema = z.object({
  scope: ScopeStringSchema.default("all"),
  /** URL-encoded filter AST, parsed and bounded by the compiler. */
  filter: z.string().max(20_000).optional(),
  q: z.string().trim().max(200).optional(),
  sort: z.union([z.string(), z.array(z.string())]).optional(),
  cursor: z.string().max(4_000).optional(),
  limit: z.coerce.number().int().min(1).max(1_000).default(200),
  /** Comma-separated custom field keys. Omit for all of them. */
  fields: z.string().max(2_000).optional(),
});

export type TrackQueryParams = z.infer<typeof TrackQueryParamsSchema>;

const TrackAlbumSchema = z.object({
  id: SpotifyIdSchema,
  name: z.string().nullable(),
  release_date: z.string().nullable(),
  album_type: z.string().nullable(),
  cover_url: z.string().nullable(),
});

const TrackArtistRefSchema = z.object({ id: SpotifyIdSchema, name: z.string().nullable() });

const TrackStatusSchema = z.object({
  status: z.enum(STATUS_IDS),
  follow_up: IsoDate.nullable(),
  drive_url: z.string().nullable(),
});

const TrackGenreSchema = z.object({
  id: LocalIdSchema,
  name: z.string(),
  slug: z.string(),
  color: z.string().nullable(),
  source: z.enum(GENRE_SOURCES),
});

const TrackAudioSchema = z.object({
  provider: z.string(),
  tempo: z.number().nullable(),
  music_key: z.number().nullable(),
  mode: z.number().nullable(),
  time_signature: z.number().nullable(),
  energy: z.number().nullable(),
  danceability: z.number().nullable(),
  valence: z.number().nullable(),
  acousticness: z.number().nullable(),
  instrumentalness: z.number().nullable(),
  speechiness: z.number().nullable(),
  liveness: z.number().nullable(),
  loudness: z.number().nullable(),
});

const TrackStatsSchema = z.object({
  play_count: z.number(),
  total_ms: z.number(),
  skip_count: z.number(),
  first_played_at: IsoDateTime.nullable(),
  last_played_at: IsoDateTime.nullable(),
});

/**
 * One row as the grid wants it: the mirror columns, plus every user-layer value
 * hydrated in the same response so the client never fans out per row.
 */
export const TrackRowSchema = z.object({
  id: SpotifyIdSchema,
  uri: z.string(),
  name: z.string(),
  cover_url: z.string().nullable(),
  duration_ms: z.number().nullable(),
  track_number: z.number().nullable(),
  disc_number: z.number().nullable(),
  explicit: DbBool,
  popularity: z.number().nullable(),
  isrc: z.string().nullable(),
  external_url: z.string().nullable(),
  /** Scope-dependent: liked date, or the date it entered this playlist. */
  added_at: z.string().nullable(),
  /** True when the track is in `liked_tracks` (mirror of Liked Songs). */
  liked: DbBool,
  album: TrackAlbumSchema.nullable(),
  artists: z.array(TrackArtistRefSchema),
  status: TrackStatusSchema.nullable(),
  genres: z.array(TrackGenreSchema),
  fields: z.record(z.string(), z.unknown()),
  audio: TrackAudioSchema.nullable(),
  /** Display BPM: user override, else AcousticBrainz, else provider tempo. */
  bpm: z.number().nullable(),
  /** True when `bpm` is this user's manual value and must not be overwritten. */
  bpm_manual: DbBool,
  stats: TrackStatsSchema.nullable(),
});

export type TrackRow = z.infer<typeof TrackRowSchema>;

export const TrackListSchema = z.object({
  rows: z.array(TrackRowSchema),
  /** Opaque keyset cursor. `null` means this was the last page. */
  next_cursor: z.string().nullable(),
  /** Rows the filter matches, ignoring pagination. */
  total: z.number(),
  /** Echoed back so a client can tell what it actually got. */
  scope: ScopeStringSchema,
  sort: z.array(SortSchema),
});

export type TrackList = z.infer<typeof TrackListSchema>;
